import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useAuthStore } from '@/stores/useAuthStore';
import { useBabyStore } from '@/stores/useBabyStore';
import { Disclaimer } from '@/components/ui/Disclaimer';
import { isGerman } from '@/i18n/locale';
import LandingDE from './LandingDE';

export default function Landing() {
  // Auto-route German speakers to the German landing.
  // Detection runs once per render via navigator.language; no manual
  // override yet. Keep this hook before any other hooks below so the
  // rule-of-hooks order doesn't change between locales.
  if (isGerman()) return <LandingDE />;

  return <LandingEN />;
}

function LandingEN() {
  const { user, signOut } = useAuthStore();
  const resetBabies = useBabyStore((s) => s.reset);
  const navigate = useNavigate();
  const isAuthed = Boolean(user);

  const onSignOut = async () => {
    await signOut();
    resetBabies();
  };

  return (
    <div className="min-h-[100dvh] bg-cream">
      <nav className="relative z-20 mx-auto max-w-6xl px-6 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center shadow-sm">
            <span className="text-white text-base font-display font-semibold">A</span>
          </div>
          <span className="font-display text-xl font-semibold text-slate tracking-tight">
            Alma
          </span>
        </Link>
        <div className="flex items-center gap-2">
          {isAuthed ? (
            <>
              <button onClick={onSignOut} className="btn-ghost text-sm">
                Sign out
              </button>
              <button onClick={() => navigate('/today')} className="btn-primary text-sm">
                Continue
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn-ghost text-sm">
                Sign in
              </Link>
              <Link to="/start" className="btn-primary text-sm">
                Get started
              </Link>
            </>
          )}
        </div>
      </nav>

      <Hero isAuthed={isAuthed} />
      <MumQuestions isAuthed={isAuthed} />
      <TrustStrip />
      <DramaticMoment />
      <Coverage />
      <AgeTuned />
      <ProductSurfaces />
      <Pricing isAuthed={isAuthed} />
      <FAQ />
      <FinalCta isAuthed={isAuthed} />

      <footer className="relative mx-auto max-w-6xl px-6 py-10 border-t border-primary-50">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-muted">
          <div>© {new Date().getFullYear()} Alma · Not a replacement for professional medical advice.</div>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-slate">Privacy</Link>
            <Link to="/imprint" className="hover:text-slate">Imprint</Link>
          </div>
        </div>
        <div className="mt-6">
          <Disclaimer className="text-center text-slate-muted text-[11px] leading-relaxed max-w-2xl mx-auto" />
        </div>
      </footer>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// HERO — rewritten per first-mum feedback:
//   · Subhead now lists concrete topics (sleep, feeding, rashes, etc.)
//     so visitors instantly recognize their use case, not abstract AI.
//   · Primary CTA "Ask Alma free →" replaces generic "Start free →"
//   · Trust row replaces the bland "Free to start · No signup" line —
//     answers "is this safe?" without claiming things we haven't done.
// ════════════════════════════════════════════════════════════════════
function Hero({ isAuthed }: { isAuthed: boolean }) {
  return (
    <section className="relative overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(70rem 45rem at 85% -10%, rgba(232,164,160,0.18) 0%, transparent 60%), radial-gradient(60rem 40rem at -10% 110%, rgba(143,181,158,0.18) 0%, transparent 55%)',
        }}
      />

      <div className="relative mx-auto max-w-6xl px-6 pt-12 pb-20 sm:pt-16 sm:pb-24">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/80 border border-primary-100 px-3 py-1 text-xs text-primary-700 mb-6 shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              Your baby's first 24 months
            </div>

            <h1 className="font-display text-5xl sm:text-6xl lg:text-[68px] font-semibold tracking-tight text-slate leading-[1.02]">
              Every <span className="italic text-primary">"is this normal?"</span>{' '}
              question,
              <br />
              answered like a friend.
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-slate-muted leading-relaxed max-w-xl">
              Calm, age-specific answers for sleep, feeding, rashes, crying, milestones —
              and "should I call the doctor?" moments. Built on pediatric guidelines,
              tuned to your baby's exact week.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              {isAuthed ? (
                <Link to="/today" className="btn-primary text-base px-6 py-3.5 text-center shadow-sm">
                  Continue to your day →
                </Link>
              ) : (
                <>
                  <Link
                    to="/start"
                    className="btn-primary text-base px-6 py-3.5 text-center shadow-sm hover:shadow-md transition-shadow"
                  >
                    Ask Alma free →
                  </Link>
                  <Link
                    to="/login"
                    className="btn-ghost text-base px-6 py-3.5 text-center"
                  >
                    I have an account
                  </Link>
                </>
              )}
            </div>

            {!isAuthed && (
              <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-slate-muted">
                <span className="inline-flex items-center gap-1.5">
                  <span className="text-primary">✓</span>Built on pediatric guidelines
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="text-primary">✓</span>Age-specific answers
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="text-primary">✓</span>Clear red flags
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="text-primary">✓</span>Private by design
                </span>
              </div>
            )}
          </div>

          <div className="lg:col-span-6 flex justify-center lg:justify-end">
            <PhoneMockup />
          </div>
        </div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════
// PHONE MOCKUP — rewritten per feedback:
//   The fever-101°F scenario was the FIRST impression and made mums
//   feel like Alma is for emergencies, not daily life. Replaced with
//   a high-frequency anxiety case (evening crying / "witching hour"),
//   which is the actual question a real mum will type in week 7.
//   Emergency detection is still showcased — but in the DramaticMoment
//   section below (which the feedback explicitly said is fine).
// ════════════════════════════════════════════════════════════════════
function PhoneMockup() {
  return (
    <div className="relative">
      <div
        aria-hidden
        className="absolute -inset-8 rounded-[60px] opacity-40 blur-2xl"
        style={{
          background:
            'radial-gradient(circle at 50% 50%, rgba(232,164,160,0.4), rgba(143,181,158,0.3) 40%, transparent 70%)',
        }}
      />

      <div className="relative w-[300px] sm:w-[340px] rounded-[44px] bg-slate p-2 shadow-2xl ring-1 ring-slate/10">
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-28 h-6 bg-slate rounded-b-2xl z-10" />
        <div className="relative rounded-[36px] bg-cream overflow-hidden h-[640px] sm:h-[680px]">
          <div className="px-7 pt-3 pb-2 flex items-center justify-between text-[11px] text-slate font-medium">
            <span>9:41</span>
            <div className="flex items-center gap-1">
              <span>●●●●</span>
            </div>
          </div>

          <div className="px-4 py-3 border-b border-primary-50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-primary flex items-center justify-center">
                <span className="text-white text-[10px] font-display font-semibold">A</span>
              </div>
              <span className="font-display text-sm font-semibold text-slate">Alma</span>
            </div>
            <div className="text-[10px] text-primary-700 bg-primary-50 px-2 py-0.5 rounded-full">
              Maya · 7 weeks
            </div>
          </div>

          {/* User message */}
          <div className="px-3 mt-4 flex justify-end">
            <div className="max-w-[80%] rounded-2xl rounded-br-md bg-primary text-white px-3 py-2 text-[12px] leading-relaxed">
              My 7-week-old cries every evening around 7pm — is this normal?
            </div>
          </div>

          {/* Alma reply — calm, reassuring, age-specific, mentions red flags */}
          <div className="px-3 mt-3">
            <div className="max-w-[88%] rounded-2xl rounded-bl-md bg-white border border-primary-50 shadow-sm px-3 py-2.5 text-[11px] leading-relaxed text-slate">
              <p>
                Yes — this is one of the most common things in the newborn world. Evening
                fussiness peaks around 6–8 weeks for almost every baby, and usually starts
                to fade between 3 and 4 months.
              </p>
              <p className="mt-2">
                What helps most parents: a quiet wind-down before the fussy window, holding
                her in motion (carrier, gentle bouncing), white noise, lower lights.
              </p>
              <p className="mt-2 text-slate-muted">
                Call your pediatrician if it comes with fever, vomiting, or she's hard to
                console for hours — otherwise, this is well within normal.
              </p>
            </div>
          </div>

          <div className="absolute bottom-4 left-3 right-3 rounded-full bg-white border border-primary-100 px-4 py-2.5 text-[11px] text-slate-muted shadow-sm">
            Ask Alma anything…
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// MUM QUESTIONS — NEW section per feedback.
// "Common mum questions" placed right below the hero so visitors
// instantly recognize themselves. Each card auto-loads the question
// into a fresh anonymous chat via /start?prompt=...
// Reframes the product from "emergency tool" to "daily companion."
// ════════════════════════════════════════════════════════════════════
function MumQuestions({ isAuthed }: { isAuthed: boolean }) {
  // 6 high-frequency anxiety questions from the feedback document.
  // Curated to span the full 24-month arc: newborn through toddler.
  const questions = [
    { q: 'Why is my newborn grunting?', age: '0–4 weeks' },
    { q: 'Is green poop normal?', age: '0–6 months' },
    { q: 'Why is my baby crying every evening?', age: '6–12 weeks' },
    { q: 'How much should my 3-month-old sleep?', age: '3 months' },
    { q: 'Is this rash urgent?', age: 'Any age' },
    { q: 'When should I worry about a fever?', age: 'Any age' },
  ];

  // Anonymous mums go to /start with the prompt prefilled; the prompt
  // is forwarded to /chat after the 15-second onboarding completes.
  // Authed mums skip onboarding and go straight to chat.
  const linkFor = (q: string) => {
    const param = encodeURIComponent(q);
    return isAuthed ? `/chat?prompt=${param}` : `/start?prompt=${param}`;
  };

  return (
    <section className="relative">
      <div className="mx-auto max-w-6xl px-6 pt-2 pb-16 sm:pt-4 sm:pb-20">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-primary-700 mb-3">
            Try a real question
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-slate tracking-tight leading-tight">
            The questions <span className="italic text-primary">every new mom</span> ends up googling.
          </h2>
          <p className="mt-4 text-base text-slate-muted">
            Tap one to see Alma's answer. No signup needed.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {questions.map((item) => (
            <Link
              key={item.q}
              to={linkFor(item.q)}
              className="group rounded-2xl border border-primary-100 bg-white/80 hover:bg-white hover:border-primary-200 hover:shadow-sm transition-all p-5 text-left"
            >
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-muted">
                {item.age}
              </div>
              <div className="mt-2 font-display text-[15px] sm:text-base text-slate leading-snug">
                {item.q}
              </div>
              <div className="mt-3 text-[12px] text-primary-700 font-medium inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
                Ask Alma
                <span aria-hidden>→</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function TrustStrip() {
  return (
    <section className="relative border-y border-primary-50 bg-white/40 backdrop-blur">
      <div className="mx-auto max-w-6xl px-6 py-6 sm:py-8 grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8 items-center text-center">
        <div>
          <div className="font-display text-2xl font-semibold text-slate">AAP</div>
          <div className="text-[11px] text-slate-muted uppercase tracking-wide mt-1">Guidelines &amp; vaccine schedule</div>
        </div>
        <div>
          <div className="font-display text-2xl font-semibold text-slate">CDC</div>
          <div className="text-[11px] text-slate-muted uppercase tracking-wide mt-1">Milestone checklists</div>
        </div>
        <div>
          <div className="font-display text-2xl font-semibold text-slate">WHO</div>
          <div className="text-[11px] text-slate-muted uppercase tracking-wide mt-1">Growth standards</div>
        </div>
        <div>
          <div className="font-display text-2xl font-semibold text-slate">112</div>
          <div className="text-[11px] text-slate-muted uppercase tracking-wide mt-1">
            Curated journey cards
          </div>
        </div>
      </div>
    </section>
  );
}

function DramaticMoment() {
  const steps = [
    {
      time: '0s',
      title: 'It\'s 11pm and the thermometer reads 101°F',
      body: 'Your 3-week-old feels hot. You\'re alone, second-guessing everything. You open Alma instead of Google.',
      tone: 'parent',
    },
    {
      time: '3s',
      title: 'Alma classifies the question as ER-tier',
      body: 'Fever ≥100.4°F in a baby under 3 months is an automatic emergency, per AAP. Alma knows that. Hard-coded rule, not AI guess.',
      tone: 'system',
    },
    {
      time: '5s',
      title: 'A red banner appears',
      body: '"A fever of 100.4 °F or higher in a baby under 3 months always needs a doctor tonight. Go to the nearest emergency room now." No softening. No second-guessing.',
      tone: 'alert',
    },
    {
      time: '8s',
      title: 'You read the explanation. You call.',
      body: 'Alma walks you through what the ER will do (bloodwork, urine test) so you\'re not blindsided. You grab the diaper bag.',
      tone: 'parent',
    },
  ];

  return (
    <section className="relative">
      <div className="mx-auto max-w-6xl px-6 py-20 sm:py-28">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-primary-700 mb-3">
            When it matters most
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold text-slate tracking-tight leading-tight">
            Alma knows when to reassure you —
            <br />
            <span className="italic text-primary">and when to tell you to call a doctor.</span>
          </h2>
          <p className="mt-5 text-base text-slate-muted leading-relaxed">
            Most questions are calm. A few are urgent. Here's what happens when the
            thermometer reads 101°F at 11pm.
          </p>
        </div>

        <div className="relative">
          <div
            aria-hidden
            className="absolute left-7 top-7 bottom-7 w-px bg-gradient-to-b from-primary-200 via-primary-100 to-transparent hidden sm:block"
          />

          <div className="space-y-5 sm:space-y-7">
            {steps.map((step, i) => {
              const dotColor =
                step.tone === 'alert'
                  ? 'bg-danger text-white'
                  : step.tone === 'system'
                    ? 'bg-primary text-white'
                    : 'bg-primary-50 text-primary-700 border border-primary-100';
              return (
                <div key={i} className="relative flex gap-4 sm:gap-5">
                  <div className="shrink-0 relative z-10">
                    <div
                      className={
                        'w-14 h-14 rounded-2xl flex items-center justify-center font-display font-semibold text-sm shadow-sm ' +
                        dotColor
                      }
                    >
                      {step.time}
                    </div>
                  </div>
                  <div
                    className={
                      'flex-1 rounded-2xl border p-5 transition-shadow ' +
                      (step.tone === 'alert'
                        ? 'border-danger/30 bg-danger/5'
                        : 'border-primary-50 bg-white shadow-sm')
                    }
                  >
                    <h3 className="font-display font-semibold text-slate text-lg leading-snug">
                      {step.title}
                    </h3>
                    <p className="mt-2 text-[15px] text-slate-muted leading-relaxed">{step.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-10 text-center">
          <p className="text-sm text-slate-muted italic">
            This is the actual response from the actual product. Not a marketing approximation.
          </p>
        </div>
      </div>
    </section>
  );
}

function Coverage() {
  const topics = [
    { icon: '🤱', title: 'Feeding', body: 'Latch, formula, solids, allergens, picky eating.' },
    { icon: '🌙', title: 'Sleep', body: 'Schedules, regressions, sleep training methods.' },
    { icon: '🩺', title: 'Health', body: 'Fevers, rashes, when to call vs ER. Triage that knows the AAP thresholds.' },
    { icon: '🌱', title: 'Milestones', body: 'CDC milestones, what to watch for, when to ask the pediatrician.' },
    { icon: '💛', title: 'Postpartum mood', body: 'Warm, judgment-free support — and the National Maternal Mental Health Hotline, PSI or 988 when it matters.' },
    { icon: '💉', title: 'Vaccines', body: 'The AAP schedule, what each well-child visit covers, what to ask.' },
    { icon: '🍽', title: 'Nutrition', body: 'Age-tuned meal plans in cups and spoons. 7 recipes a week.' },
    { icon: '🦷', title: 'Teething', body: 'When to expect each tooth. What it actually feels like.' },
  ];

  return (
    <section className="relative bg-white/60 border-y border-primary-50">
      <div className="mx-auto max-w-6xl px-6 py-20 sm:py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-primary-700 mb-3">
            What Alma covers
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold text-slate tracking-tight leading-tight">
            The whole first <span className="italic text-primary">two years.</span>
          </h2>
          <p className="mt-5 text-base text-slate-muted leading-relaxed">
            Not just a chatbot. A growing journey of weekly cards, age-tuned nutrition, and
            evidence-based answers to anything that comes up.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {topics.map((t) => (
            <div
              key={t.title}
              className="rounded-2xl border border-primary-50 bg-cream/60 hover:bg-white hover:shadow-sm transition-all p-5"
            >
              <div className="text-3xl">{t.icon}</div>
              <div className="font-display font-semibold text-slate mt-3">{t.title}</div>
              <div className="text-[13px] text-slate-muted mt-1.5 leading-relaxed">{t.body}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AgeTuned() {
  const bands = [
    {
      label: '0–6 months',
      tag: 'Newborn',
      title: 'The exhausted weeks',
      body: 'Latch troubleshooting, cluster feeding reassurance, the 6-week growth spurt, safe sleep, the "is this colic" question. The right tone for someone who hasn\'t slept.',
      bg: 'from-primary-50 to-cream',
    },
    {
      label: '6–12 months',
      tag: 'First foods',
      title: 'Solids, sitting, and surprise teeth',
      body: 'Iron-first solids, allergen introduction (per LEAP study), gagging vs choking, the 8-month sleep regression, first molars, separation anxiety peak.',
      bg: 'from-accent/15 to-cream',
    },
    {
      label: '12–24 months',
      tag: 'Toddler',
      title: 'Words, opinions, and food strikes',
      body: 'Bottle weaning, vocabulary spurts, the M-CHAT autism screening, tantrums explained, picky eating without battles, screen-time guidance, potty-training readiness.',
      bg: 'from-primary-50 to-accent/10',
    },
  ];

  return (
    <section className="relative">
      <div className="mx-auto max-w-6xl px-6 py-20 sm:py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-primary-700 mb-3">
            Built for every age
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold text-slate tracking-tight leading-tight">
            Advice for an 11-week-old
            <br />
            <span className="italic text-primary">isn't the same as for 14.</span>
          </h2>
          <p className="mt-5 text-base text-slate-muted leading-relaxed">
            Alma doesn't give generic baby advice. Every answer is tuned to the exact week of
            your baby's life — because biology is.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {bands.map((band) => (
            <div
              key={band.label}
              className={
                'rounded-2xl border border-primary-50 p-6 bg-gradient-to-br ' + band.bg
              }
            >
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold">
                <span className="text-primary-700">{band.label}</span>
                <span className="text-slate-muted/40">·</span>
                <span className="text-slate-muted">{band.tag}</span>
              </div>
              <h3 className="font-display text-xl font-semibold text-slate mt-3 leading-snug">
                {band.title}
              </h3>
              <p className="mt-2.5 text-sm text-slate leading-relaxed">{band.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductSurfaces() {
  const surfaces = [
    {
      eyebrow: 'Ask anything',
      title: 'Chat',
      body: 'Type or ask. Get a friend\'s answer that knows your baby\'s exact age. Safety-classified emergency responses with red triage banners. Disclaimers without the lawyering.',
      visual: <ChatPreview />,
    },
    {
      eyebrow: 'Tuned to your week',
      title: 'This Week',
      body: '112 evidence-based journey cards across 24 months. Past, this week, and coming up — so you\'re prepared, not surprised.',
      visual: <CardPreview />,
    },
    {
      eyebrow: 'Age-tuned eating',
      title: 'Nutrition',
      body: '7 real recipes a week in cups and spoons, with grams for precision. AAP-aligned guidance for every stage from breast milk to toddler picky-eating.',
      visual: <NutritionPreview />,
    },
  ];

  return (
    <section className="relative bg-white/60 border-y border-primary-50">
      <div className="mx-auto max-w-6xl px-6 py-20 sm:py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-primary-700 mb-3">
            Three surfaces, one app
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold text-slate tracking-tight leading-tight">
            Not just chat.
            <br />
            <span className="italic text-primary">A whole companion.</span>
          </h2>
        </div>

        <div className="space-y-12">
          {surfaces.map((s, i) => (
            <div
              key={s.title}
              className={
                'grid lg:grid-cols-2 gap-8 lg:gap-12 items-center ' +
                (i % 2 === 1 ? 'lg:[&>*:first-child]:order-2' : '')
              }
            >
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-primary-700 mb-3">
                  {s.eyebrow}
                </div>
                <h3 className="font-display text-3xl font-semibold text-slate tracking-tight leading-snug">
                  {s.title}
                </h3>
                <p className="mt-4 text-base text-slate-muted leading-relaxed">{s.body}</p>
              </div>
              <div>{s.visual}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ChatPreview() {
  return (
    <div className="rounded-2xl border border-primary-100 bg-cream p-5 shadow-sm">
      <div className="flex justify-end mb-3">
        <div className="max-w-[80%] rounded-2xl rounded-br-md bg-primary text-white px-3.5 py-2 text-[13px]">
          When does the 4-month sleep regression actually end?
        </div>
      </div>
      <div className="flex justify-start">
        <div className="max-w-[88%] rounded-2xl rounded-bl-md bg-white border border-primary-50 shadow-sm px-3.5 py-2.5 text-[13px] leading-relaxed text-slate">
          For most babies, 2–6 weeks. The "regression" is actually permanent — sleep cycles
          mature and stay that way — but the disruption settles as your baby learns to
          connect cycles. The sleep itself never goes back to newborn-style. The chaos
          does.
        </div>
      </div>
    </div>
  );
}

function CardPreview() {
  return (
    <div className="rounded-2xl border border-primary-100 bg-white p-5 shadow-sm relative overflow-hidden">
      <div
        aria-hidden
        className="absolute left-0 top-0 bottom-0 w-1.5 bg-primary-300"
      />
      <div className="pl-2">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-9 h-9 rounded-xl bg-primary-50 flex items-center justify-center text-lg">
            🌱
          </div>
          <div className="text-[10px] uppercase tracking-wide font-medium text-slate-muted">
            Milestone · 14 months
          </div>
        </div>
        <h4 className="font-display text-lg font-semibold text-slate leading-snug">
          First molars — the worst teething phase
        </h4>
        <p className="mt-2 text-[13px] text-slate leading-relaxed">
          The first molars usually arrive between 12 and 18 months. Most parents describe
          them as the hardest teething round — bigger surface area, more pressure on the
          gum, a few days of misery while each tooth breaks through.
        </p>
        <div className="mt-3 flex items-center gap-2">
          <span className="text-[11px] text-primary-700 font-medium">Ask Alma about this</span>
          <span className="text-slate-muted/40">·</span>
          <span className="text-[11px] text-slate-muted">Mark as read</span>
        </div>
      </div>
    </div>
  );
}

function NutritionPreview() {
  return (
    <div className="rounded-2xl bg-gradient-to-br from-accent/15 to-accent/5 border border-accent/30 p-5">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">🌿</span>
        <span className="text-[11px] font-semibold text-accent-600 uppercase tracking-wider">
          Tuesday · 12–18 months
        </span>
      </div>
      <h4 className="font-display text-lg font-semibold text-slate mt-1 leading-snug">
        Mini frittata cups
      </h4>
      <p className="mt-2 text-[13px] text-slate leading-relaxed">
        4 eggs · ¼ cup whole milk · a large handful of spinach · ½ small zucchini · ⅓ cup
        grated cheddar. Bake at 350°F for 20 min. Each cup is a self-contained meal.
      </p>
      <div className="mt-3 flex flex-wrap gap-1.5">
        <span className="text-[10px] font-medium bg-accent/10 text-accent-600 border border-accent/20 px-1.5 py-0.5 rounded">
          Egg
        </span>
        <span className="text-[10px] font-medium bg-accent/10 text-accent-600 border border-accent/20 px-1.5 py-0.5 rounded">
          Dairy
        </span>
      </div>
    </div>
  );
}

function Pricing({ isAuthed }: { isAuthed: boolean }) {
  return (
    <section className="relative">
      <div className="mx-auto max-w-6xl px-6 py-20 sm:py-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-primary-700 mb-3">
            Simple pricing
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold text-slate tracking-tight leading-tight">
            Free to start.
            <br />
            <span className="italic text-primary">Pro is coming soon.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
          <PricingCard
            name="Free"
            price="$0"
            period="forever"
            tagline="Perfect for trying Alma."
            features={[
              '3 questions to start, no signup',
              '20 questions a month after signup',
              'Full journey for one baby',
              'Age-synced reminders',
            ]}
            cta={isAuthed ? 'Continue' : 'Start free'}
            ctaLink={isAuthed ? '/today' : '/start'}
            highlight={false}
          />
          <PricingCard
            name="Pro"
            price="$9.99"
            period="per month at launch"
            tagline="For the questions that don't wait."
            features={[
              'Unlimited questions',
              'Up to 3 baby profiles',
              '90-day conversation history',
              'Voice & image input',
            ]}
            cta="See what's coming"
            ctaLink="/pricing"
            highlight={true}
          />
        </div>

        <p className="mt-8 text-center text-sm text-slate-muted">
          Pro isn't available to buy yet. Everyone on the free plan hears first when it launches.
        </p>
      </div>
    </section>
  );
}

function PricingCard({
  name,
  price,
  period,
  tagline,
  features,
  cta,
  ctaLink,
  highlight,
}: {
  name: string;
  price: string;
  period: string;
  tagline: string;
  features: string[];
  cta: string;
  ctaLink: string;
  highlight: boolean;
}) {
  return (
    <div
      className={
        'rounded-2xl p-7 ' +
        (highlight
          ? 'bg-slate text-white shadow-lg ring-1 ring-slate'
          : 'bg-white border border-primary-100')
      }
    >
      {highlight && (
        <div className="inline-flex items-center px-2.5 py-1 rounded-full bg-accent/20 text-accent-300 text-[11px] font-semibold uppercase tracking-wider mb-4">
          Coming soon
        </div>
      )}
      <div className={'text-sm font-medium ' + (highlight ? 'text-white/70' : 'text-slate-muted')}>
        {name}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className={'font-display text-5xl font-semibold ' + (highlight ? 'text-white' : 'text-slate')}>
          {price}
        </div>
        <div className={'text-sm ' + (highlight ? 'text-white/60' : 'text-slate-muted')}>{period}</div>
      </div>
      <p className={'mt-2 text-sm ' + (highlight ? 'text-white/70' : 'text-slate-muted')}>
        {tagline}
      </p>

      <ul className="mt-6 space-y-3">
        {features.map((f) => (
          <li
            key={f}
            className={'flex items-start gap-2.5 text-sm ' + (highlight ? 'text-white/90' : 'text-slate')}
          >
            <span className={highlight ? 'text-accent-300' : 'text-primary'}>✓</span>
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <Link
        to={ctaLink}
        className={
          'mt-7 block w-full text-center rounded-xl py-3 font-medium text-sm transition-colors ' +
          (highlight
            ? 'bg-white text-slate hover:bg-cream'
            : 'bg-primary text-white hover:bg-primary-600')
        }
      >
        {cta} →
      </Link>
    </div>
  );
}

function FAQ() {
  const items = [
    {
      q: 'Is this a replacement for my pediatrician?',
      a: 'No, and we go out of our way to never act like one. Alma never diagnoses. Every health response ends with "this is general guidance, not medical advice." For anything urgent, Alma points you to your pediatrician\'s after-hours line or the ER. We work with your pediatrician, not around them.',
    },
    {
      q: 'How is this different from googling?',
      a: 'Google gives you 50 articles for any age, by any author, with any agenda. Alma gives you one answer tuned to your baby\'s exact age, grounded in AAP, CDC and WHO guidance, in plain language. And it doesn\'t panic-mode you toward the worst possible diagnosis the way Google does.',
    },
    {
      q: 'How does Alma handle real emergencies?',
      a: 'Hard-coded safety rules that run before the AI. A fever of 100.4°F or higher in a baby under 3 months, trouble breathing, blue lips or a baby who won\'t wake up → an immediate "go to the ER / call 911" response and a red banner. Mentions of self-harm → the 988 Lifeline, the National Maternal Mental Health Hotline and PSI, immediately. These responses never depend on the AI getting it right.',
    },
    {
      q: 'Is my data private?',
      a: 'Your messages are stored only in your account, on servers in Switzerland, encrypted at rest and in transit. We never sell data and never share it with advertisers. Message content is never sent to analytics platforms. Want everything deleted? Email hello@alma.baby and it\'s gone within 30 days.',
    },
    {
      q: 'What if my baby is older than 24 months?',
      a: 'Alma is currently focused on the first 24 months — that\'s where pediatric guidance matters most and where parents need it most. Beyond 2 years, the questions shift dramatically and we don\'t want to over-promise. Sign up anyway — when we extend the journey, you\'ll be the first to know.',
    },
    {
      q: 'Can I share Alma with my partner or family?',
      a: 'Family plans are coming soon. For now, both parents can use the same account from any device — your account, your baby, your data, accessible by you both.',
    },
  ];

  const [openIdx, setOpenIdx] = useState<number | null>(0);

  return (
    <section className="relative bg-white/60 border-y border-primary-50">
      <div className="mx-auto max-w-3xl px-6 py-20 sm:py-24">
        <div className="text-center mb-12">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-primary-700 mb-3">
            Questions
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold text-slate tracking-tight leading-tight">
            Things parents <span className="italic text-primary">actually ask.</span>
          </h2>
        </div>

        <div className="space-y-3">
          {items.map((item, i) => {
            const isOpen = openIdx === i;
            return (
              <div
                key={i}
                className={
                  'rounded-xl border transition-colors ' +
                  (isOpen ? 'border-primary-200 bg-white shadow-sm' : 'border-primary-50 bg-white/50 hover:bg-white')
                }
              >
                <button
                  type="button"
                  onClick={() => setOpenIdx(isOpen ? null : i)}
                  className="w-full text-left px-5 py-4 flex items-center justify-between gap-4"
                  aria-expanded={isOpen}
                >
                  <span className="font-medium text-slate text-[15px]">{item.q}</span>
                  <span
                    className={
                      'shrink-0 text-slate-muted transition-transform ' + (isOpen ? 'rotate-45' : '')
                    }
                    aria-hidden
                  >
                    +
                  </span>
                </button>
                {isOpen && (
                  <div className="px-5 pb-4 text-[14px] text-slate-muted leading-relaxed">
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FinalCta({ isAuthed }: { isAuthed: boolean }) {
  return (
    <section className="relative overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-50"
        style={{
          background:
            'radial-gradient(50rem 30rem at 50% 50%, rgba(232,164,160,0.2) 0%, transparent 60%)',
        }}
      />
      <div className="relative mx-auto max-w-3xl px-6 py-20 sm:py-28 text-center">
        <h2 className="font-display text-4xl sm:text-5xl font-semibold text-slate tracking-tight leading-tight">
          Stop Googling at 3am.
          <br />
          <span className="italic text-primary">Start sleeping again.</span>
        </h2>
        <p className="mt-5 text-base text-slate-muted leading-relaxed max-w-xl mx-auto">
          No signup to start. Two questions to begin. Free for your first three.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          {isAuthed ? (
            <Link to="/today" className="btn-primary text-base px-8 py-3.5">
              Continue to your day →
            </Link>
          ) : (
            <Link to="/start" className="btn-primary text-base px-8 py-3.5 shadow-sm hover:shadow-md transition-shadow">
              Ask Alma free →
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
